import {
  DataResult,
  State as DataState,
  FilterOperator,
} from '@progress/kendo-data-query';
import {
  GridCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { produce } from 'immer';
import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ModalAlertImg from '../../../../../common/assets/images/merchants/modal-attention.svg';
import { ProductStatusTypes } from '../../../../../common/enums';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../../common/global/helpers';
import useMount from '../../../../../common/hooks/useMount';
import Button from '../../../../../common/widgets/Button';
import { DataLoader } from '../../../../../common/widgets/DataLoader';
import Dialog from '../../../../../common/widgets/Dialog';
import Grid, { GridCellTd, getSkip } from '../../../../../common/widgets/Grid';
import Icons from '../../../../../common/widgets/Icons';
import Input from '../../../../../common/widgets/Input';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../../common/widgets/Notification';
import PopoverDialog from '../../../../../common/widgets/PopoverDialog';
import { config } from '../../../config';
import { getProductStatusInfo, productStatusColor } from '../../../constants';
import { AccountInfoContext } from '../../../contexts/AccountInfoContext';
import { ProductItemModel, ProductStatusModel } from '../../../models/products';
import { resources } from '../../../resources/resources';
import { productsService } from '../../../services/products';
import { EmptyStateNoProducts } from '../../Common/EmptyStates';
import { ActionsDropdown, getStockCell } from '../Common/List';
import {
  clearProductSentForReviewInfoMessage,
  getProductSentForReviewData,
  setProductSentForReviewInfoMessage,
} from '../Common/utils';
import { CancelProductApprovalDialog } from './CancelProductApprovalDialog';
import { CancelProductDeactivationDialog } from './CancelProductDeactivationDialog';
import { DeactivateProductDialog } from './DeactivateProductDialog';
import { DeleteDraftDialog } from './DeleteDraftDialog';
import { SendProductForApprovalConfirmationDialog } from './SendProductApprovalDialogs/ConfirmationDialog';
import { InvalidProductForApprovalDialog } from './SendProductApprovalDialogs/InvalidProductDialog';

const initialDataState: DataState = {
  skip: 0,
  take: config.grid.defaultPageSize,
  sort: [{ field: 'name', dir: 'asc' }],
};

export const ProductsList = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const odataString = searchParams.get('dataState');
  const [products, setProducts] = useState<ProductItemModel[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [dataState, setDataState] = useState<DataState>(
    odataString ? JSON.parse(decodeURI(odataString)) : initialDataState,
  );
  const [showErrorPanel, setShowErrorPanel] = useState<boolean | undefined>();
  const [statuses, setStatuses] = useState<ProductStatusModel[] | undefined>();
  const [editWarningDialogProductId, setEditWarningDialogProductId] = useState<
    number | undefined
  >();
  const [deactiavateDialogProductId, setDeactiavateDialogProductId] = useState<
    number | undefined
  >();
  const [cancelApprovalProductId, setCancelApprovalProductId] = useState<
    number | undefined
  >();
  const [cancelDeactivationProductId, setCancelDeactivationProductId] =
    useState<number | undefined>();
  const [sendForApprovalProduct, setSendForApprovalProduct] = useState<
    ProductItemModel | undefined
  >();
  const [
    invalidProductForApprovalProductId,
    setInvalidProductForApprovalProductId,
  ] = useState<number | undefined>();
  const [deleteDraftProduct, setDeleteDraftProduct] = useState<
    ProductItemModel | undefined
  >();
  const [activeStatus, setActiveStatus] = useState<
    ProductStatusTypes | undefined
  >();
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
  const { accountInfo } = useContext(AccountInfoContext);

  const isLoading = _.isNil(statuses) || _.isNil(products);

  const loadStatuses = async (abortController?: AbortController) => {
    const resp = await productsService.getProductStatuses({
      signal: abortController?.signal,
    });
    if (abortController?.signal.aborted) return;
    if (resp.hasErrors) {
      setShowErrorPanel(true);
    } else {
      setStatuses(resp.data);
    }
  };

  useMount(loadStatuses);

  const dataStateChange = (e: GridDataStateChangeEvent) => {
    navigate(
      `/products?dataState=${encodeURI(
        JSON.stringify({
          ...e.dataState,
          skip: getSkip(dataState, e.dataState),
        }),
      )}`,
    );
  };

  useEffect(() => {
    setDataState(
      odataString ? JSON.parse(decodeURI(odataString)) : initialDataState,
    );
  }, [odataString]);

  const onDataReceived = (data: DataResult, showErrorPanel?: boolean) => {
    setProducts(data.data);
    setTotal(data.total);
    setShowErrorPanel(showErrorPanel);
  };

  const onStatusChange = (status?: ProductStatusTypes) => {
    setActiveStatus(status);
    const newDataState = getFilteredDataState(dataState, status, searchTerm);
    navigate(`/products?dataState=${encodeURI(JSON.stringify(newDataState))}`);
  };

  const onSearchTermChange = (searchTerm: string | undefined) => {
    setSearchTerm(searchTerm);
    const newDataState = getFilteredDataState(
      dataState,
      activeStatus,
      searchTerm,
    );
    navigate(`/products?dataState=${encodeURI(JSON.stringify(newDataState))}`);
  };

  const onProductSentForDeactivation = (productId: number) => {
    showSuccessToast(translate(resources.products.deactivate.dialog.success));
    setDeactiavateDialogProductId(undefined);

    const product = products?.find(e => e.productId === productId);
    if (!product) return;

    product.statusId = ProductStatusTypes.PendingDeactivation;
    product.status =
      statuses?.find(e => e.id === ProductStatusTypes.PendingDeactivation)
        ?.name ?? '';
  };

  const onCancelApproval = (productId: number) => {
    showSuccessToast(
      translate(resources.products.list.cancelProductApproval.success),
    );
    setCancelApprovalProductId(undefined);

    const product = products?.find(e => e.productId === productId);
    if (!product) return;

    product.statusId = ProductStatusTypes.Draft;
    product.status =
      statuses?.find(e => e.id === ProductStatusTypes.Draft)?.name ?? '';
  };

  const onCancelDeactivation = (productId: number) => {
    showSuccessToast(
      translate(resources.products.list.cancelProductDeactivation.success),
    );
    setCancelDeactivationProductId(undefined);

    const product = products?.find(e => e.productId === productId);
    if (!product) return;

    product.statusId = ProductStatusTypes.Active;
    product.status =
      statuses?.find(e => e.id === ProductStatusTypes.Active)?.name ?? '';
  };

  const onProductSentForApproval = (product: ProductItemModel) => {
    setProductSentForReviewInfoMessage(
      resources.products.list.sentForReview,
      {
        0: product.name,
      },
      accountInfo.manufacturerId,
    );
    setSendForApprovalProduct(undefined);

    const p = products?.find(e => e.productId === product.productId);
    if (!p) return;

    p.statusId = ProductStatusTypes.PendingApproval;
    p.status =
      statuses?.find(e => e.id === ProductStatusTypes.PendingApproval)?.name ??
      '';
  };

  const onDraftDeleted = (product: ProductItemModel) => {
    setProducts(
      produce(d => d?.filter(e => e.productId !== product.productId) ?? []),
    );
    setDeleteDraftProduct(undefined);
    showSuccessToast(translate(resources.products.list.deleteDraft.success));
  };

  const sentForReviewData = getProductSentForReviewData(
    accountInfo.manufacturerId,
  );

  const onExportClick = async () => {
    setIsExportLoading(true);
    const resp = await productsService.downloadProductsExcel(
      translate(resources.products.list.title),
    );
    setIsExportLoading(false);
    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.generic));
    }
  };

  return (
    <div className="m-products m-all-products">
      <div className="m-page-actions">
        <h1>{translate(resources.products.list.title)}</h1>
        {!isLoading && (
          <div className="m-page-action">
            <Button
              className="btn btn-outline"
              icon={<Icons.MExport />}
              onClick={onExportClick}
              loading={isExportLoading}
            >
              <span className="btn-text">
                {translate(resources.products.list.export)}
              </span>
            </Button>
            <Button
              className="btn btn-solid"
              onClick={() => navigate('/products/add')}
              icon={<Icons.MAddProduct />}
            >
              <span className="btn-text">
                {translate(resources.products.list.addProduct)}
              </span>
            </Button>
          </div>
        )}
      </div>
      {!!sentForReviewData && (
        <Notification
          type={NotificationType.Info}
          showClose
          onCloseClick={() =>
            clearProductSentForReviewInfoMessage(accountInfo.manufacturerId)
          }
          messages={[
            {
              message: translate(
                sentForReviewData.resourceKey,
                sentForReviewData.payload,
              ) as string,
            },
          ]}
          controlClassName="product-validation-info"
        />
      )}
      <div className="m-panel">
        <DataLoader
          getData={productsService.getProducts}
          dataState={dataState}
          onDataReceived={onDataReceived}
        />
        <Loader
          loading={isLoading}
          loader={<LoadingSpinner />}
          showErrorPanel={showErrorPanel}
        >
          {!!products && !!statuses && (
            <>
              <FilterPanel
                statuses={statuses}
                activeStatus={activeStatus}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                onStatusChange={onStatusChange}
                onSearchTermChange={onSearchTermChange}
              />
              {products.length ? (
                <div className="m-grid m-products-grid">
                  <Grid
                    data={products}
                    pageable={{
                      pageSizes: config.grid.pageSizes,
                      pageSizeValue: dataState.take,
                      info: false,
                      previousNext: true,
                    }}
                    {...dataState}
                    onDataStateChange={dataStateChange}
                    total={total}
                    sortable={{ allowUnsort: true, mode: 'multiple' }}
                    columns={[
                      {
                        field: 'name',
                        title: translate(resources.products.list.columns.name),
                      },
                      {
                        field: 'status',
                        title: translate(
                          resources.products.list.columns.status,
                        ),
                        cell: ({ dataItem }: GridCellProps) => (
                          <GridCellTd className="grid-center">
                            <PopoverDialog
                              type="hover"
                              placement="bottom"
                              animation={false}
                              content={
                                <div style={{ width: 200 }}>
                                  {getProductStatusInfo(
                                    dataItem.statusId,
                                    translate,
                                  )}
                                </div>
                              }
                            >
                              <span
                                style={{
                                  backgroundColor:
                                    productStatusColor[
                                      dataItem.statusId as ProductStatusTypes
                                    ],
                                }}
                                className="m-grid-badge"
                              >
                                {dataItem.status}
                              </span>
                            </PopoverDialog>
                          </GridCellTd>
                        ),
                        headerClassName: 'grid-center',
                      },
                      {
                        field: 'categories',
                        title: translate(
                          resources.products.list.columns.categories,
                        ),
                        sortable: false,
                      },
                      {
                        field: 'productType',
                        title: translate(
                          resources.products.list.columns.productType,
                        ),
                      },
                      {
                        field: 'stock',
                        title: translate(resources.products.list.columns.stock),
                        cell: ({ dataItem }: GridCellProps) =>
                          getStockCell(dataItem),
                        headerClassName: 'grid-center',
                      },
                      {
                        title: translate(resources.actions),
                        cell: ({ dataItem }) => (
                          <GridCellTd className="grid-center">
                            <PopoverDialog
                              className="popover-actions-list"
                              type="click"
                              placement="bottom"
                              closeFromContent
                              appendTo={document.body}
                              content={
                                <ActionsDropdown
                                  product={dataItem}
                                  showEditWarningDialog={() =>
                                    setEditWarningDialogProductId(
                                      dataItem.productId,
                                    )
                                  }
                                  showDeactivateProductDialog={() => {
                                    setDeactiavateDialogProductId(
                                      dataItem.productId,
                                    );
                                  }}
                                  showCancelApprovalDialog={() => {
                                    setCancelApprovalProductId(
                                      dataItem.productId,
                                    );
                                  }}
                                  showCancelDeactivationDialog={() => {
                                    setCancelDeactivationProductId(
                                      dataItem.productId,
                                    );
                                  }}
                                  showSendProductForApprovalDialog={() =>
                                    setSendForApprovalProduct(dataItem)
                                  }
                                  showInvalidProductForApprovalDialog={() =>
                                    setInvalidProductForApprovalProductId(
                                      dataItem.productId,
                                    )
                                  }
                                  showDeleteDraftDialog={() =>
                                    setDeleteDraftProduct(dataItem)
                                  }
                                />
                              }
                            >
                              <a className="m-grid-action"></a>
                            </PopoverDialog>
                          </GridCellTd>
                        ),
                        headerClassName: 'grid-center',
                      },
                    ]}
                  />
                </div>
              ) : (
                <EmptyStateNoProducts />
              )}
            </>
          )}
        </Loader>
      </div>
      {editWarningDialogProductId && (
        <EditWarningDialog
          closeDialog={() => setEditWarningDialogProductId(undefined)}
          productId={editWarningDialogProductId}
        />
      )}
      {deactiavateDialogProductId && (
        <DeactivateProductDialog
          closeDialog={() => setDeactiavateDialogProductId(undefined)}
          productId={deactiavateDialogProductId}
          productName={
            products?.find(e => e.productId === deactiavateDialogProductId)
              ?.name ?? ''
          }
          onProductSentForDeactivation={onProductSentForDeactivation}
        />
      )}
      {cancelApprovalProductId && (
        <CancelProductApprovalDialog
          closeDialog={() => setCancelApprovalProductId(undefined)}
          productId={cancelApprovalProductId}
          onCancelApproval={onCancelApproval}
        />
      )}
      {cancelDeactivationProductId && (
        <CancelProductDeactivationDialog
          closeDialog={() => setCancelDeactivationProductId(undefined)}
          productId={cancelDeactivationProductId}
          onCancelDeactivation={onCancelDeactivation}
        />
      )}
      {invalidProductForApprovalProductId && (
        <InvalidProductForApprovalDialog
          closeDialog={() => setInvalidProductForApprovalProductId(undefined)}
          productId={invalidProductForApprovalProductId}
        />
      )}
      {sendForApprovalProduct && (
        <SendProductForApprovalConfirmationDialog
          closeDialog={() => setSendForApprovalProduct(undefined)}
          product={sendForApprovalProduct}
          onProductSentForApproval={onProductSentForApproval}
        />
      )}
      {deleteDraftProduct && (
        <DeleteDraftDialog
          closeDialog={() => setDeleteDraftProduct(undefined)}
          product={deleteDraftProduct}
          onDraftDeleted={onDraftDeleted}
        />
      )}
    </div>
  );
};

const FilterPanel = (props: {
  statuses: ProductStatusModel[];
  activeStatus: ProductStatusTypes | undefined;
  onStatusChange: (status?: ProductStatusTypes) => void;
  searchTerm: string | undefined;
  setSearchTerm: (term: string | undefined) => void;
  onSearchTermChange: (searchTerm: string | undefined) => void;
}) => {
  const {
    statuses,
    activeStatus,
    onStatusChange,
    searchTerm,
    setSearchTerm,
    onSearchTermChange,
  } = props;
  const { t: translate } = useTranslation();
  const getClassName = (status: ProductStatusTypes | undefined) => {
    if (activeStatus === status) return 'm-data-active-filter';
  };
  const [isSearchInputVisible, setIsSearchInputVisible] =
    useState<boolean>(false);
  const [hasBeenRendered, setHasBeenRendered] = useState<boolean>(false);
  const searchRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let timeoutId: any;
    if (hasBeenRendered) {
      timeoutId = setTimeout(() => {
        onSearchTermChange(searchTerm);
      }, 700);
    }
    setHasBeenRendered(true);
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (isSearchInputVisible && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isSearchInputVisible]);

  return (
    <div className="m-data-utility">
      <ul className="m-data-filters">
        <li>
          <span
            onClick={() => onStatusChange()}
            className={getClassName(undefined)}
          >
            {translate(resources.all)}
          </span>
        </li>
        {statuses.map(e => (
          <li key={e.id}>
            <span
              onClick={() => onStatusChange(e.id)}
              className={getClassName(e.id)}
            >
              {e.name}
            </span>
          </li>
        ))}
      </ul>
      <div className="m-data-search">
        {isSearchInputVisible && (
          <div className="m-data-search-input">
            <Input
              type="text"
              placeholder={translate(resources.products.list.searchPlaceholder)}
              value={searchTerm ?? ''}
              onChange={e => setSearchTerm(e.value)}
              ref={searchRef}
            />
            <Button
              onClick={() => onSearchTermChange(searchTerm)}
              icon={<Icons.MDataSearch />}
            />
          </div>
        )}
        <Button
          className="m-data-search-opener"
          onClick={() => {
            setSearchTerm(undefined);
            onSearchTermChange(undefined);
            setIsSearchInputVisible(!isSearchInputVisible);
          }}
          icon={
            isSearchInputVisible ? (
              <Icons.MDataCloseSearch />
            ) : (
              <Icons.MDataSearch />
            )
          }
        ></Button>
      </div>
    </div>
  );
};

const EditWarningDialog = (props: {
  productId: number;
  closeDialog: () => void;
}) => {
  const { productId, closeDialog } = props;
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  return (
    <Dialog closeDialog={closeDialog} showCloseIcon className="dialog-s">
      <div className="dialog-content">
        <div className="dialog-image m-dialog-img">
          <img
            src={ModalAlertImg}
            alt={translate(resources.products.edit.warningDialog.title)}
          />
        </div>
        <div className="dialog-title">
          {translate(resources.products.edit.warningDialog.title)}
        </div>
        <div className="dialog-text centered">
          {translate(resources.products.edit.warningDialog.body)}
        </div>
      </div>
      <div className="dialog-actions horizontal">
        <Button className="btn btn-outline" onClick={closeDialog}>
          <span className="btn-text">{translate(resources.abort)}</span>
        </Button>
        <Button
          className="btn btn-solid"
          onClick={() => navigate(`/products/edit/${productId}`)}
        >
          <span className="btn-text">{translate(resources.continue)}</span>
        </Button>
      </div>
    </Dialog>
  );
};

const getFilteredDataState = (
  dataState: DataState,
  status?: ProductStatusTypes,
  searchTerm?: string,
): DataState => {
  return produce(dataState, d => {
    if (!_.isNil(status) && searchTerm) {
      d.filter = {
        logic: 'and',
        filters: [
          {
            field: 'statusId',
            operator: FilterOperator.EqualTo,
            value: status,
          },
          {
            field: 'tolower(name)',
            operator: FilterOperator.Contains,
            value: searchTerm.toLowerCase(),
          },
        ],
      };
    }
    if (!_.isNil(status) && !searchTerm) {
      d.filter = {
        logic: 'and',
        filters: [
          {
            field: 'statusId',
            operator: FilterOperator.EqualTo,
            value: status,
          },
        ],
      };
    }
    if (_.isNil(status) && searchTerm) {
      d.filter = {
        logic: 'and',
        filters: [
          {
            field: 'tolower(name)',
            operator: FilterOperator.Contains,
            value: searchTerm.toLowerCase(),
          },
        ],
      };
    }
    if (_.isNil(status) && !searchTerm) {
      d.filter = {
        logic: 'and',
        filters: [],
      };
    }
  });
};
